import React, { useEffect, useState } from 'react';
import { Stack, TextField, Typography } from '@mui/material';
import dayjs from 'dayjs';

import { ConfigSection } from '../../../common';
import { Button } from '../../../components/core';
import { useMerchant } from '../../../contexts/MerchantContext';
import {
  useKbStatusQuery,
  useStartKbCrawlMutation,
} from '../../../store/modules/ellaDataLayer/api';
import type { KbStatusResponse } from '../../../store/modules/ellaDataLayer/types';
import { toast } from '../../../util/toast';
import { FORM_MAX_WIDTH } from '../ellaFormStyles';

/** A crawl takes a few minutes; status is re-read this often while one runs. */
export const KB_STATUS_POLL_MS = 15_000;

const formatTime = (iso: string): string => dayjs(iso).format('MMM D, YYYY h:mm A');

/** Status lines for the section: what the knowledge base holds, then how the last crawl went. */
export const describeKbStatus = (status: KbStatusResponse): string[] => {
  const lines = [
    status.documents > 0 && status.lastCrawledAt
      ? `${status.documents.toLocaleString()} articles in the knowledge base, last crawled ${formatTime(status.lastCrawledAt)}.`
      : 'No articles in the knowledge base yet.',
  ];
  const run = status.lastRun;
  if (run?.state === 'running') {
    lines.push(
      `Crawl running${run.startedAt ? ` since ${formatTime(run.startedAt)}` : ''}. It takes a few minutes.`,
    );
  } else if (run?.state === 'failed') {
    lines.push(
      `The last crawl failed${run.completedAt ? ` (${formatTime(run.completedAt)})` : ''}. Check the kb-crawler-job logs in Cloud Run.`,
    );
  } else if (run?.state === 'succeeded' && status.documents === 0) {
    lines.push(
      'The last crawl found no articles. The help center may block crawlers or need JavaScript to show its pages.',
    );
  }
  return lines;
};

/**
 * Catches typos before the request; the data layer runs the full check. A comma would split the
 * crawler's comma-separated URL list, so it is refused here too.
 */
const isHttpsUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && url.hostname.includes('.') && !value.includes(',');
  } catch {
    return false;
  }
};

/** Start URLs from the field: one or more https addresses separated by spaces. */
export const parseHelpCenterUrls = (value: string): string[] | null => {
  const urls = value.split(/\s+/).filter(Boolean);
  if (!urls.length || !urls.every(isHttpsUrl)) {
    return null;
  }
  return urls;
};

const getCrawlErrorMessage = (err: unknown): string => {
  const error = (err as { data?: { error?: string } })?.data?.error;
  return error || 'Failed to start the crawl, please try again';
};

/**
 * Crawls the merchant's help center into the knowledge base Ella answers from.
 * Skipify admins only; renders nothing for anyone else.
 */
export const HelpCenterCrawlSection = (): JSX.Element | null => {
  const { merchantId, isSkipifyAdmin } = useMerchant();
  const [pollingInterval, setPollingInterval] = useState(0);
  const { data: status, isError } = useKbStatusQuery(
    { merchantId },
    { skip: !isSkipifyAdmin || !merchantId, pollingInterval },
  );
  const [startKbCrawl, { isLoading: isStarting }] = useStartKbCrawlMutation();
  // Null until the admin types, so the field shows the last crawl's URLs once status arrives.
  const [typedUrls, setTypedUrls] = useState<string | null>(null);
  const [urlError, setUrlError] = useState<string | undefined>();
  // The last run as it was when a crawl started. Until the status shows a different run, the new
  // one is not visible yet, so the button stays locked and polling stays on.
  const [runAtStart, setRunAtStart] = useState<string | undefined>();

  const isRunning = status?.lastRun?.state === 'running';
  const lastRunKey = JSON.stringify(status?.lastRun ?? null);
  const isAwaitingRun = runAtStart === lastRunKey;
  useEffect(() => {
    setPollingInterval(isRunning || isAwaitingRun ? KB_STATUS_POLL_MS : 0);
  }, [isRunning, isAwaitingRun]);

  if (!isSkipifyAdmin) {
    return null;
  }

  const urlsValue = typedUrls ?? status?.lastRun?.helpCenterUrls.join(' ') ?? '';

  const crawl = async () => {
    const helpCenterUrls = parseHelpCenterUrls(urlsValue);
    if (!helpCenterUrls) {
      setUrlError('Enter the help center address, e.g. https://help.store.com/en-US');
      return;
    }
    setUrlError(undefined);
    const runBefore = lastRunKey;
    try {
      await startKbCrawl({ merchantId, helpCenterUrls }).unwrap();
      setRunAtStart(runBefore);
      toast.success('Crawl started. It takes a few minutes.');
    } catch (err) {
      toast.error(getCrawlErrorMessage(err));
    }
  };

  return (
    <ConfigSection
      title="Help center crawl"
      description="Skipify admins only. Reads the merchant's help center into the knowledge base Ella answers from. Run it again after the help center changes."
      marginBottom={2}
    >
      <Stack spacing={2.5} sx={{ maxWidth: FORM_MAX_WIDTH }} data-testid="ella-kb-crawl-section">
        {status && (
          <Stack spacing={0.5} data-testid="ella-kb-crawl-status">
            {describeKbStatus(status).map((line) => (
              <Typography key={line} variant="small" color="text.secondary" sx={{ m: 0 }}>
                {line}
              </Typography>
            ))}
          </Stack>
        )}
        {isError && (
          <Typography variant="small" color="error" sx={{ m: 0 }}>
            Could not load the knowledge base status.
          </Typography>
        )}
        <TextField
          label="Help center URL"
          placeholder="https://help.store.com/en-US"
          value={urlsValue}
          onChange={(event) => {
            setTypedUrls(event.target.value);
            setUrlError(undefined);
          }}
          error={!!urlError}
          helperText={
            urlError ??
            'The crawler reads pages on this host under this path. Separate several with spaces.'
          }
          inputProps={{ 'aria-label': 'Help center URL' }}
          InputLabelProps={{ shrink: true }}
        />
        <Stack direction="row" justifyContent="flex-start">
          <Button
            text="Crawl help center"
            theme="secondary"
            onClick={crawl}
            loading={isStarting}
            disabled={isStarting || isRunning || isAwaitingRun || !merchantId}
            dataTestId="ella-kb-crawl-button"
            sx={{ height: 48, minWidth: 180, whiteSpace: 'nowrap' }}
          />
        </Stack>
      </Stack>
    </ConfigSection>
  );
};
